import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  PauseCircle as PauseCircleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { 
  getTaskStatusColor, 
  getTaskItemTypeColor,
  getTaskPriorityColor,
  getTaskItemTypeIcon,
  getTaskPriorityIcon
} from '../../utils/helper';
import { getTaskItemTypeLabel } from '../../utils/lead_helper_v2';

// Styled card component for timeline view
const DayViewCardContainer = styled(Box)(({ theme, borderColor, statusColor }) => ({
  backgroundColor: '#FFFFFF',
  border: `1px solid ${borderColor || '#E2E8F0'}`,
  borderLeft: `3px solid ${statusColor || '#9CA3AF'}`,
  borderRadius: '12px',
  padding: '16px',
  margin: '8px 0',
  minHeight: '100px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: '#F8FAFC',
    borderColor: '#CBD5E1',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)',
  },
}));

const DayViewCard = ({ 
  item, 
  onClick, 
  showLead = true 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Format call time
  const formatCallTime = (startTime) => {
    try {
      if (!startTime) return '';
      const time = new Date(startTime);
      return format(time, 'h:mm a');
    } catch (error) {
      return '';
    }
  };

  // Check if event is in the past
  const isPastEvent = (startTime) => {
    try {
      if (!startTime) return false;
      const eventTime = new Date(startTime);
      const now = new Date();
      return eventTime < now;
    } catch (error) {
      return false;
    }
  };

  const pastEvent = isPastEvent(item.start_time);

  return (
    <DayViewCardContainer 
      borderColor="#E2E8F0"
      statusColor={getTaskPriorityColor(item.priority).border}
      backgroundColor={getTaskPriorityColor(item.priority).background}
      onClick={() => onClick && onClick(item)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      overflow="hidden"
    >


      {/* Vertical Layout: 5 Rows */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 0.5
      }}>
        {/* Row 1: Title (up to 2 lines) */}
        <Typography
          variant="h5"
          sx={{
            fontSize: '0.8125rem', // Reduced for better fit
            fontWeight: 700,
            color: '#1E293B',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.2,
            maxHeight: '2.4em' // 2 lines * 1.2 line height
          }}
        >
          {item.title}
        </Typography>

        {/* Row 2: Separate badges for Date, Time, and Duration */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {/* Date Badge */}
          <Box
            sx={{
              backgroundColor: '#F3F4F6',
              borderRadius: '6px',
              padding: '2px 6px',
              border: '1px solid #E5E7EB',
              display: 'inline-block'
            }}
          >
            <Typography
              sx={{
                fontSize: '0.5625rem', // Reduced for better fit
                color: '#374151',
                fontWeight: 600
              }}
            >
              {item.date_verbose || (item.start_time ? format(new Date(item.start_time), 'MMM dd') : 'Today')}
            </Typography>
          </Box>

          {/* Time Badge */}
          <Box
            sx={{
              backgroundColor: '#F3F4F6',
              borderRadius: '6px',
              padding: '2px 6px',
              border: '1px solid #E5E7EB',
              display: 'inline-block'
            }}
          >
            <Typography
              sx={{
                fontSize: '0.5625rem', // Reduced for better fit
                color: '#374151',
                fontWeight: 600
              }}
            >
              {item.start_time_verbose || (item.start_time ? format(new Date(item.start_time), 'h:mm a') : 'No time')}
            </Typography>
          </Box>

          {/* Duration Badge (if available) */}
          {item.duration && (
            <>
              {/* Separator */}
              <Typography sx={{ fontSize: '0.625rem', color: '#64748B', flexShrink: 0 }}>|</Typography>

              <Box
                sx={{
                  backgroundColor: '#F3F4F6',
                  borderRadius: '6px',
                  padding: '2px 6px',
                  border: '1px solid #E5E7EB',
                  display: 'inline-block'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.5625rem', // Reduced for better fit
                    color: '#374151',
                    fontWeight: 600
                  }}
                >
                  {item.duration}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Row 3: Agent */}
        {item.assigned_to && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon sx={{ fontSize: '0.75rem', color: '#3B82F6' }} />
            <Typography
              sx={{
                fontSize: '0.625rem', // Reduced for better fit
                color: '#374151',
                fontWeight: 500
              }}
            >
              {item.assigned_to.name}
            </Typography>
          </Box>
        )}

        {/* Row 4: Lead (if available) */}
        {showLead && item.lead && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BusinessIcon sx={{ fontSize: '0.75rem', color: '#10B981' }} />
            <Typography
              sx={{
                fontSize: '0.625rem', // Reduced for better fit
                color: '#374151',
                fontWeight: 500
              }}
            >
              {item.lead.name} {item.lead.contact && `(${item.lead.contact})`}
            </Typography>
          </Box>
        )}

        {/* Row 5: Type | Status | Priority */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
          minHeight: '16px'
        }}>
          {/* Type */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            minWidth: 0,
            flexShrink: 0
          }}>
            {getTaskItemTypeIcon(item.item_type)}
            <Typography
              sx={{
                fontSize: '0.5625rem', // Reduced for better fit
                color: getTaskItemTypeColor(item.item_type).border,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textTransform: 'none'
              }}
            >
              {getTaskItemTypeLabel(item.item_type, item.item_type_verbose)}
            </Typography>
          </Box>

          {/* Separator */}
          <Typography sx={{ fontSize: '0.625rem', color: '#64748B', flexShrink: 0 }}>|</Typography>

          {/* Status */}
          <Typography
            sx={{
              fontSize: '0.5625rem', // Reduced for better fit
              color: getTaskStatusColor(item.status).border,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flexShrink: 0
            }}
          >
            {item.status_verbose || item.status?.split('_').join(' ')}
          </Typography>
           {/* Separator */}
           <Typography sx={{ fontSize: '0.625rem', color: '#64748B', flexShrink: 0 }}>|</Typography>

          {/* Priority */}
          <Typography
            sx={{
              fontSize: '0.5625rem', // Reduced for better fit
              color: getTaskPriorityColor(item.priority).border,
              fontWeight: 600,
              textTransform: 'capitalize',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flexShrink: 0
            }}
          >
            {item.priority || 'Medium'}
          </Typography>
        </Box>
      </Box>
    </DayViewCardContainer>
  );
};

export default DayViewCard; 